import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Store, MapPin, Globe, Plus, Search, Star, Tag, Coins, Copy, Check, QrCode, ExternalLink, Crown, Trash2, AlertTriangle, Loader2, ShieldAlert, Smartphone, ArrowRight, X, BadgeCheck } from 'lucide-react';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useAppContext } from '../hooks/useAppContext';
import { BCHLogo } from '../components/Layout';
import { PLATFORM_FEE_ADDRESS } from '../lib/platform';
import { useBusinessDirectory } from '../hooks/useBusinessDirectory';
import { categoryLabel, type BusinessProfile } from '../lib/business';
import PayBusinessButton from '../components/PayBusinessButton';
import { nip19 } from 'nostr-tools';
import { useNip05Verified } from '../hooks/useNip05Verified';
import { useAllBusinessVerifications } from '../hooks/useBusinessVerification';
import { computeVerificationState, type VerificationState } from '../lib/businessVerify';
import VerifiedBusinessBadge from '../components/VerifiedBusinessBadge';


const MERCHANT_KIND = 30023; // Long-form content for listings
const MERCHANT_TAG = 'bch-merchant';
const FEATURED_TAG = 'bch-merchant-featured';

// Admin pubkeys — can remove any listing (e.g. malicious URLs)
const ADMIN_PUBKEYS = [
  'df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc', // BCH Reply Guy
];

// Pricing in USD
const LISTING_FEE_USD = 1; // $1 to publish a listing
const FEATURED_FEE_USD = 5; // $5/month to get featured (highlighted + top placement)

const CATEGORIES = ['All', 'Food & Drink', 'Retail', 'Services', 'Online', 'Entertainment', 'Technology', 'Other'];

export default function MerchantDirectoryPage() {
  const { user } = useAppContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const isAdmin = !!user && ADMIN_PUBKEYS.includes(user.pubkey);

  const { events: listings, loading, refetch } = useNostrQuery({
    filters: [{ kinds: [MERCHANT_KIND], '#t': [MERCHANT_TAG], limit: 100 }],
  });

  // Fetch featured listing payments (kind 9735 with featured tag)
  const { events: featuredPayments } = useNostrQuery({
    filters: [{ kinds: [9735], '#t': [FEATURED_TAG], limit: 50 }],
  });

  // Fetch deletion events from admins + all users for their own listings
  const deletionAuthors = user ? [...new Set([...ADMIN_PUBKEYS, user.pubkey])] : ADMIN_PUBKEYS;
  const { events: deletionEvents } = useNostrQuery({
    filters: deletionAuthors.length > 0
      ? [{ kinds: [5], authors: deletionAuthors, '#k': [String(MERCHANT_KIND)], limit: 200 }]
      : [],
    enabled: deletionAuthors.length > 0,
  });

  // Build set of deleted listing IDs and a-tag coordinates
  const deletedIds = new Set<string>();
  deletionEvents.forEach(ev => {
    ev.tags.forEach((t: string[]) => {
      if (t[0] === 'e') deletedIds.add(t[1]);
      if (t[0] === 'a') deletedIds.add(t[1]);
    });
  });

  // Also track locally deleted IDs for instant removal before relay propagation
  const [localDeletedIds, setLocalDeletedIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('bchnostr:deleted-merchants');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });

  const handleListingDeleted = (eventId: string, aTag: string) => {
    const updated = new Set(localDeletedIds);
    updated.add(eventId);
    updated.add(aTag);
    setLocalDeletedIds(updated);
    try { localStorage.setItem('bchnostr:deleted-merchants', JSON.stringify([...updated])); } catch {}
  };

  // Determine which listings are featured (paid within last 30 days)
  const featuredIds = new Set<string>();
  const thirtyDaysAgo = Math.floor(Date.now() / 1000) - 30 * 86400;
  featuredPayments.forEach(ev => {
    if (ev.created_at > thirtyDaysAgo) {
      const dTag = ev.tags.find(t => t[0] === 'd')?.[1];
      if (dTag) featuredIds.add(dTag);
      // Also check for 'e' tag referencing listing
      const eTag = ev.tags.find(t => t[0] === 'e')?.[1];
      if (eTag) featuredIds.add(eTag);
    }
  });

  const parsedListings = listings.map(ev => {
    try {
      const data = JSON.parse(ev.content);
      const dTag = ev.tags.find(t => t[0] === 'd')?.[1] || '';
      return { ...data, event: ev, pubkey: ev.pubkey, dTag, featured: featuredIds.has(ev.id) || featuredIds.has(dTag) || data.featured };
    } catch {
      return { title: 'Unknown', description: ev.content, event: ev, pubkey: ev.pubkey, dTag: '', featured: false };
    }
  }).filter(l => {
    // Hide deleted listings
    if (deletedIds.has(l.event.id)) return false;
    if (l.dTag && deletedIds.has(`${MERCHANT_KIND}:${l.pubkey}:${l.dTag}`)) return false;
    if (localDeletedIds.has(l.event.id)) return false;
    if (l.dTag && localDeletedIds.has(`${MERCHANT_KIND}:${l.pubkey}:${l.dTag}`)) return false;

    if (searchQuery && !l.title?.toLowerCase().includes(searchQuery.toLowerCase()) && !l.description?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (category !== 'All' && l.category !== category) return false;
    return true;
  });

  // Sort: featured first, then by date
  parsedListings.sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return b.event.created_at - a.event.created_at;
  });

  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Store className="w-5 h-5 text-primary" /> BCH Merchants
          </h1>
          {user && (
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" /> Add Listing
            </button>
          )}
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search merchants..."
            className="w-full bg-muted rounded-lg pl-10 pr-4 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                category === cat ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* List-your-business CTA — points to the profile Business toggle */}
      {user && (
        <div className="px-4 pt-3">
          <Link
            to="/settings"
            className="flex items-center gap-3 rounded-xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 to-emerald-500/5 p-3 hover:border-primary transition-colors group"
          >
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <Store className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold">Own a business that takes BCH?</p>
              <p className="text-xs text-muted-foreground">Turn on your Business Profile and get listed here.</p>
            </div>
            <ArrowRight className="w-4 h-4 text-primary shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      )}

      {/* BCHPay.cash Promotion Banner */}
      <BCHPayBanner />

      {/* Create Listing Form */}
      {showCreateForm && (
        <CreateMerchantForm onCreated={() => { setShowCreateForm(false); refetch(); }} />
      )}

      {/* BCH Businesses (kind 30499 profiles) */}
      <BusinessSection category={category} searchQuery={searchQuery} />

      {/* Listings Grid */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        ) : parsedListings.length === 0 ? (
          <div className="text-center py-16">
            <Store className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">No merchants found.</p>
            {user && (
              <button onClick={() => setShowCreateForm(true)} className="text-sm text-primary hover:underline mt-2">
                Be the first to add a listing!
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {parsedListings.map((listing, i) => (
              <MerchantCard key={listing.event.id || i} listing={listing} user={user} isAdmin={isAdmin} onDeleted={handleListingDeleted} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── BCH Businesses (kind 30499) ───────────────────────────────────
// Maps the directory's label-based category filter to business category
// ids so the two share one filter bar.
const LABEL_TO_BIZ_CAT: Record<string, string> = {
  'All': 'all',
  'Food & Drink': 'food',
  'Retail': 'retail',
  'Services': 'services',
  'Online': 'online',
  'Entertainment': 'entertainment',
  'Technology': 'technology',
  'Other': 'other',
};

function BusinessSection({ category, searchQuery }: { category: string; searchQuery: string }) {
  const bizCat = LABEL_TO_BIZ_CAT[category] || 'all';
  const { businesses, loading } = useBusinessDirectory(bizCat);

  const filtered = businesses.filter(b => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      b.name.toLowerCase().includes(q) ||
      (b.description || '').toLowerCase().includes(q) ||
      (b.location || '').toLowerCase().includes(q)
    );
  });

  // One batched query for ALL verification grants, mapped per business, so a
  // long directory doesn't fire a query per card. Grants are author-filtered
  // to trusted issuers inside the hook — the real trust boundary.
  const { grants } = useAllBusinessVerifications();
  const verifyState = useMemo(() => {
    const m = new Map<string, VerificationState>();
    for (const g of grants) {
      if (!g) continue;
      m.set(g.businessPubkey, computeVerificationState(g, null));
    }
    return m;
  }, [grants]);

  if (loading && businesses.length === 0) return null;
  if (filtered.length === 0) return null;

  return (
    <div className="px-4 pt-4">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-primary flex items-center gap-1.5">
          <BadgeCheck className="w-4 h-4" /> BCH Businesses
        </h2>
        <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-full font-bold">
          {filtered.length}
        </span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {filtered.map(b => (
          <BusinessCard key={b.pubkey} business={b} verifyState={verifyState.get(b.pubkey) || null} />
        ))}
      </div>
    </div>
  );
}

function BusinessCard({ business, verifyState }: { business: BusinessProfile; verifyState: VerificationState | null }) {
  const { profile } = useNostrProfile(business.pubkey);
  const verified = useNip05Verified(profile?.nip05, business.pubkey);
  const isVerifiedBusiness = verifyState?.status === 'VERIFIED';
  const npub = (() => {
    try { return nip19.npubEncode(business.pubkey); } catch { return business.pubkey; }
  })();
  const displayName = business.name || profile?.display_name || profile?.name || business.pubkey.slice(0, 8);
  const locationLabel = business.onlineOnly ? 'Online only' : business.location;

  return (
    <div className="glass-card transition-colors hover:border-primary/40">
      <Link to={`/profile/${npub}`} className="block">
      <div className="flex items-start gap-3">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0 bg-muted" />
        ) : (
          <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Store className="w-5 h-5 text-primary" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="font-semibold text-sm truncate">{displayName}</h3>
            {verified && <BadgeCheck className="w-3.5 h-3.5 text-primary shrink-0" />}
          </div>
          {isVerifiedBusiness && verifyState && (
            <div className="mt-1">
              <VerifiedBusinessBadge businessPubkey={business.pubkey} state={verifyState} size="sm" static />
            </div>
          )}
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
            <Tag className="w-3 h-3" /> {categoryLabel(business.category)}
          </span>
          {business.tagline && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{business.tagline}</p>
          )}
          <div className="flex items-center gap-3 mt-2">
            {locationLabel && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                <MapPin className="w-3 h-3 shrink-0" /> {locationLabel}
              </span>
            )}
            {business.bch && (
              <span className="flex items-center gap-1 text-xs text-primary font-medium">
                <BCHLogo size={12} /> Accepts BCH
              </span>
            )}
          </div>
        </div>
      </div>
      </Link>

      {isVerifiedBusiness && (
        <a
          href="https://bchlocal.com"
          target="_blank"
          rel="noopener noreferrer"
          className="no-navigate mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-500 hover:underline font-medium"
        >
          <MapPin className="w-3 h-3" /> View on BCH Local <ExternalLink className="w-2.5 h-2.5" />
        </a>
      )}

      {/* Pay sits outside the Link so tapping it doesn't navigate. */}
      {business.bch && (
        <div className="mt-3 no-navigate">
          <PayBusinessButton
            recipientPubkey={business.pubkey}
            address={business.bch}
            businessName={displayName}
            fullWidth
          />
        </div>
      )}
    </div>
  );
}

function BCHPayBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('bchpay-banner-dismissed') === '1'; } catch { return false; }
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('bchpay-banner-dismissed', '1'); } catch {}
  };

  return (
    <div className="mx-4 mt-4 relative overflow-hidden rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-emerald-600/10 to-primary/5">
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors z-10"
        title="Dismiss"
      >
        <X className="w-3.5 h-3.5 text-muted-foreground" />
      </button>

      {/* Subtle shimmer overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400/5 to-transparent animate-[bchpay-shimmer_4s_ease-in-out_infinite] pointer-events-none" />

      <div className="relative p-4 sm:p-5">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="hidden sm:flex w-12 h-12 rounded-xl bg-emerald-500/15 items-center justify-center shrink-0">
            <Smartphone className="w-6 h-6 text-emerald-500" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Smartphone className="w-4 h-4 text-emerald-500 sm:hidden" />
              <h3 className="font-bold text-sm sm:text-base text-foreground">
                Accept BCH at your business
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-3">
              Use <strong className="text-foreground">bchpay.cash</strong> as your free point-of-sale terminal.
              Every transaction helps grow the BCH merchant map and strengthens the network.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <a
                href="https://bchpay.cash"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors shadow-sm shadow-emerald-500/20"
              >
                Try BCH Pay <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <span className="text-[11px] text-muted-foreground">
                Free &middot; No signup &middot; Instant setup
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MerchantCard({ listing, user, isAdmin, onDeleted }: { listing: any; user: any; isAdmin: boolean; onDeleted: (eventId: string, aTag: string) => void }) {
  const { publish } = useNostrPublish();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwn = user && listing.pubkey === user.pubkey;
  const canDelete = isOwn || isAdmin;

  const handleDelete = async () => {
    if (!user) return;
    setDeleting(true);
    try {
      const aTag = `${MERCHANT_KIND}:${listing.pubkey}:${listing.dTag}`;
      await publish({
        kind: 5,
        content: isAdmin && !isOwn ? 'Removed by admin' : '',
        tags: [
          ['e', listing.event.id],
          ['a', aTag],
          ['k', String(MERCHANT_KIND)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      onDeleted(listing.event.id, aTag);
    } catch (err) {
      console.error('Failed to delete listing:', err);
    }
    setDeleting(false);
    setShowConfirm(false);
  };

  return (
    <div className={`glass-card transition-colors relative ${listing.featured ? 'ring-2 ring-amber-400/50 border-amber-400/30' : 'hover:border-primary/30'}`}>
      {listing.featured && (
        <div className="absolute -top-2 -right-2 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
          <Crown className="w-3 h-3" /> Featured
        </div>
      )}
      {/* Delete button */}
      {canDelete && (
        <button
          onClick={() => setShowConfirm(true)}
          className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
          title={isAdmin && !isOwn ? 'Remove listing (admin)' : 'Delete listing'}
        >
          {isAdmin && !isOwn ? <ShieldAlert className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
        </button>
      )}
      {listing.image && (
        <img src={listing.image} alt="" className="w-full h-36 object-cover rounded-lg mb-3" />
      )}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <BCHLogo size={24} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-sm">{listing.title || 'Unnamed Merchant'}</h3>
          {listing.category && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <Tag className="w-3 h-3" /> {listing.category}
            </span>
          )}
          {listing.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{listing.description}</p>
          )}
          <div className="flex items-center gap-3 mt-2">
            {listing.location && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="w-3 h-3" /> {listing.location}
              </span>
            )}
            {listing.website && (
              <a href={listing.website.match(/^https?:\/\//) ? listing.website : `https://${listing.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">
                <Globe className="w-3 h-3" /> Visit
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => !deleting && setShowConfirm(false)}>
          <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Remove Listing</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isAdmin && !isOwn ? 'Admin removal' : 'This cannot be undone'}
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-1">
              Remove <strong className="text-foreground">"{listing.title}"</strong> from the directory?
            </p>
            {isAdmin && !isOwn && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mb-3 flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
                You're removing someone else's listing as admin.
              </p>
            )}
            <p className="text-xs text-muted-foreground mb-5">
              A deletion request will be sent to all relays.
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-border hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateMerchantForm({ onCreated }: { onCreated: () => void }) {
  const { publish } = useNostrPublish();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Other');
  const [location, setLocation] = useState('');
  const [website, setWebsite] = useState('');
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState<'form' | 'pay' | 'publishing'>('form');
  const [bchPrice, setBchPrice] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [wantFeatured, setWantFeatured] = useState(false);

  // Fetch BCH price for fee calculation
  useEffect(() => {
    fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin-cash&vs_currencies=usd')
      .then(r => r.json())
      .then(d => setBchPrice(d['bitcoin-cash']?.usd || null))
      .catch(() => setBchPrice(400)); // fallback
  }, []);

  const totalFeeUSD = LISTING_FEE_USD + (wantFeatured ? FEATURED_FEE_USD : 0);
  const feeBCH = bchPrice ? totalFeeUSD / bchPrice : 0;
  const feeDisplay = feeBCH > 0 ? feeBCH.toFixed(6) : '...';

  const paymentAddress = PLATFORM_FEE_ADDRESS.replace('bitcoincash:', '');
  const paymentURI = `bitcoincash:${paymentAddress}?amount=${feeDisplay}&message=BCHNostr+Merchant+Listing`;

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setStep('pay');
  };

  const handleConfirmPayment = async () => {
    setStep('publishing');
    setSaving(true);
    try {
      const dTag = `merchant-${Date.now()}`;
      // Normalize website URL
      const normalizedWebsite = website.trim() && !website.trim().match(/^https?:\/\//) ? `https://${website.trim()}` : website.trim();

      await publish({
        kind: MERCHANT_KIND,
        content: JSON.stringify({ title, description, category, location, website: normalizedWebsite, featured: wantFeatured }),
        tags: [
          ['t', MERCHANT_TAG],
          ...(wantFeatured ? [['t', FEATURED_TAG]] : []),
          ['d', dTag],
          ['title', title],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      // If featured, also publish a payment receipt
      if (wantFeatured) {
        await publish({
          kind: 9735,
          content: JSON.stringify({ type: 'featured-listing', amount_usd: totalFeeUSD, listing: title }),
          tags: [
            ['t', FEATURED_TAG],
            ['d', dTag],
          ],
          created_at: Math.floor(Date.now() / 1000),
        });
      }

      onCreated();
    } catch (e) {
      console.error('Failed to create listing:', e);
      setSaving(false);
      setStep('form');
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="border-b border-border p-4 bg-muted/30">
      {step === 'form' && (
        <form onSubmit={handleFormSubmit} className="space-y-3">
          <h3 className="font-semibold text-sm">Add New Merchant</h3>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Business name"
            className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-primary"
            required
          />
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Description — what does the business offer?"
            className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-primary resize-none"
            rows={2}
          />
          <div className="grid grid-cols-2 gap-3">
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none"
            >
              {CATEGORIES.filter(c => c !== 'All').map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <input
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="City / Country"
              className="bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none"
            />
          </div>
          <input
            value={website}
            onChange={e => setWebsite(e.target.value)}
            placeholder="Website URL"
            className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-primary"
          />

          {/* BCHPay.cash CTA inside form */}
          <a
            href="https://bchpay.cash"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors group"
          >
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
              <Smartphone className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                Need a POS? Try bchpay.cash
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </p>
              <p className="text-[11px] text-muted-foreground">
                Free point-of-sale app — start accepting BCH in seconds
              </p>
            </div>
          </a>

          {/* Featured listing upsell */}
          <div
            onClick={() => setWantFeatured(!wantFeatured)}
            className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
              wantFeatured
                ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/10'
                : 'border-border hover:border-amber-400/50'
            }`}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${
              wantFeatured ? 'border-amber-400 bg-amber-400' : 'border-muted-foreground/30'
            }`}>
              {wantFeatured && <Check className="w-3 h-3 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <Crown className="w-4 h-4 text-amber-500" />
                <span className="font-semibold text-sm">Featured Listing</span>
                <span className="text-xs font-medium text-amber-600 dark:text-amber-400">${FEATURED_FEE_USD}/mo</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Highlighted at the top of the directory with a golden badge. Get more visibility.
              </p>
            </div>
          </div>

          {/* Pricing summary */}
          <div className="bg-card border border-border rounded-lg p-3 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Listing fee</span>
              <span className="font-medium">${LISTING_FEE_USD}</span>
            </div>
            {wantFeatured && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Featured (30 days)</span>
                <span className="font-medium">${FEATURED_FEE_USD}</span>
              </div>
            )}
            <div className="border-t border-border pt-1.5 flex items-center justify-between text-sm">
              <span className="font-semibold">Total</span>
              <span className="font-bold text-primary">${totalFeeUSD} <span className="text-xs text-muted-foreground font-normal">({feeDisplay} BCH)</span></span>
            </div>
          </div>

          <button
            type="submit"
            disabled={!title.trim()}
            className="w-full px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            Continue to Payment
          </button>
        </form>
      )}

      {step === 'pay' && (
        <div className="space-y-4">
          <div className="text-center">
            <Coins className="w-10 h-10 mx-auto text-primary mb-2" />
            <h3 className="font-semibold text-sm">Pay with Bitcoin Cash</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Send <strong className="text-foreground">{feeDisplay} BCH</strong> (${totalFeeUSD}) to publish your listing
            </p>
          </div>

          {/* Payment address */}
          <div className="space-y-2">
            <div className="flex items-center gap-1 bg-muted rounded-lg p-2.5">
              <code className="flex-1 text-[11px] font-mono text-muted-foreground break-all">{paymentAddress}</code>
              <button
                onClick={() => handleCopy(paymentAddress)}
                className="shrink-0 p-1.5 rounded hover:bg-background"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-muted-foreground" />}
              </button>
            </div>

            {/* Quick pay link */}
            <a
              href={paymentURI}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-colors"
            >
              <Coins className="w-4 h-4" /> Open in Wallet
            </a>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-[11px] text-amber-800 dark:text-amber-200">
              After sending payment, click "I've Paid" below to publish your listing.
              Listings are published on Nostr and appear immediately.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setStep('form')}
              className="flex-1 px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted"
            >
              Back
            </button>
            <button
              onClick={handleConfirmPayment}
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Publishing...' : "I've Paid — Publish"}
            </button>
          </div>
        </div>
      )}

      {step === 'publishing' && (
        <div className="flex flex-col items-center gap-3 py-8">
          <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Publishing your listing...</p>
        </div>
      )}
    </div>
  );
}

